import VerticalDivider from "common/misc/VerticalDivider";
import TiersLocks from "features/affiliation/common/Tiers/TiersLocks";
import {
  DarkModeGoldWithBg,
  DarkModePlatinumWithBg,
  DarkModeSilverWithBg,
  GoldWithBg,
  PlatinumWithBg,
  SilverWithBg,
} from "common/Icons/Icons";
import TiersProgress from "features/affiliation/common/Tiers/TiersProgress";
import StatisticItem from "features/affiliation/common/StatisticItem";
import Card from "common/Boxes/Card";
import useReferredUsers from "features/affiliation/common/hooks/useReferredUsers";
import { AffiliationTabs } from "features/affiliation/common/types";
import useMedia from "utils/hooks/useMedia";
import styled from "styled-components";
import { MOBILE_SEARCH, SMALL_MOBILE, TABLET } from "common/constants";
import {
  MINIFIED_TIERS,
  OVERVIEW_SMALL_SCREEN_LAYOUT,
} from "features/affiliation/common/constants";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

import { LanguageNameSpaces } from "@shared/types";

interface TiersSummaryProps {
  isMinified?: boolean;
  hideNumUsers?: boolean;
}

const TiersSummary = ({
  isMinified: isMinifiedProps,
  hideNumUsers,
}: TiersSummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const isDarkMode = useSelector(selectIsDarkMode);
  const isMinified =
    isMinifiedProps ||
    useMedia([`(max-width: ${MINIFIED_TIERS})`], [true], false);
  const autoWidth = useMedia(
    [
      `(max-width: ${MINIFIED_TIERS})`,
      `(max-width: 1050px)`,
      `(max-width: ${OVERVIEW_SMALL_SCREEN_LAYOUT})`,
      `(max-width: 1550px)`,
      `(min-width: 1550px)`,
    ],
    [false, true, false, true, false],
    false
  );
  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallMobile = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [true],
    false
  );

  const { tiers, currentTierData, isLastTier, numActivePaidUsers } =
    useReferredUsers(AffiliationTabs.REFERRED_USERS);
  const currentTier = currentTierData?.tier || 1;
  const showNumUsers = !isMobile && !hideNumUsers;

  const tierToIcon = isDarkMode
    ? TIER_TO_DARK_MODE_ICON_WITH_BG
    : TIER_TO_ICON_WITH_BG;

  return (
    <StyledCard $isTablet={isTablet}>
      {!isSmallMobile && <IconWrapper>{tierToIcon[currentTier]}</IconWrapper>}
      <TiersProgress
        currentTierData={currentTierData}
        numActivePaidUsers={numActivePaidUsers}
        showPercentage={!showNumUsers}
        style={{
          minWidth: 240,
          width: isMinified ? "100%" : autoWidth ? "auto" : "55%",
        }}
      />
      {!isMinified && (
        <TiersLocks
          tiers={tiers}
          currentTier={currentTier}
          style={{ width: autoWidth ? "auto" : "45%" }}
        />
      )}
      {showNumUsers && (
        <>
          <VerticalDivider style={{ height: 60, margin: 0 }} />
          <NumUsers>
            <StatisticItem
              title={t("tiers.active_users")}
              value={
                isLastTier
                  ? numActivePaidUsers
                  : `${numActivePaidUsers}/${currentTierData?.top || 0}`
              }
              isBottomTitle={true}
            />
          </NumUsers>
        </>
      )}
    </StyledCard>
  );
};

export default TiersSummary;

const TIER_TO_ICON_WITH_BG = {
  1: <SilverWithBg />,
  2: <GoldWithBg />,
  3: <PlatinumWithBg />,
};

const TIER_TO_DARK_MODE_ICON_WITH_BG = {
  1: <DarkModeSilverWithBg />,
  2: <DarkModeGoldWithBg />,
  3: <DarkModePlatinumWithBg />,
};

const StyledCard = styled(Card)<{
  $isTablet: boolean;
}>`
  padding: ${(p) => (p.$isTablet ? 12 : 16)}px;
  grid-gap: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const IconWrapper = styled.div`
  width: 78px;
  height: 82px;
`;
const NumUsers = styled.div`
  min-width: 125px;
`;
